import { useCallback } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSessionStore } from '@/entities/session';
import { getOperationsHistory } from '@/shared/api';
import { OPERATIONS_HISTORY_KEY } from '@/shared/config';
import { useAppSettingsStore } from '@/shared/lib';

type Params = {
  firstNextFrameId?: string;
  enabled?: boolean;
};

const FRAMES_KEY = 'frames';

export const useResetOperationsHistoryFrames = () => {
  const queryClient = useQueryClient();
  const { language } = useAppSettingsStore();
  const { session } = useSessionStore();

  return useCallback(
    () =>
      queryClient.removeQueries({
        queryKey: [
          OPERATIONS_HISTORY_KEY,
          session?.userId,
          language,
          'all',
          FRAMES_KEY,
        ],
      }),
    [queryClient, session?.userId, language]
  );
};

export const useOperationsHistoryFrames = ({
  firstNextFrameId,
  enabled = true,
}: Params) => {
  const { language } = useAppSettingsStore();
  const { session } = useSessionStore();

  return useInfiniteQuery({
    queryKey: [
      OPERATIONS_HISTORY_KEY,
      session?.userId,
      language,
      'all',
      FRAMES_KEY,
      firstNextFrameId,
    ],
    queryFn: ({ pageParam }) =>
      getOperationsHistory(
        `all?nextframeid=${encodeURIComponent(pageParam ?? '')}`
      ),
    initialPageParam: firstNextFrameId,
    getNextPageParam: lastPage => lastPage.nextFrameId || undefined,
    enabled: enabled && !!firstNextFrameId && !!session?.userId,
    staleTime: Infinity,
  });
};
